<template>
  <el-dialog
    v-model="dialogVisible"
    :width="isMobile ? '95%' : dialogWidth"
    :append-to-body="true"
    :close-on-click-modal="false"
    :close-on-press-escape="true"
    :lock-scroll="false"
    class="login-dialog"
    @close="handleClose"
  >
    <div class="login-container">
      <!-- Title -->
      <div class="login-title">
        <div class="title-main">
          {{ $t('login.welcome') }}
        </div>
        <div class="title-sub">
          {{ $t('login.platformName') }}
        </div>
      </div>

      <!-- Login Tabs -->
      <div class="login-tabs">
        <div
          class="tab-item"
          :class="{ active: activeTab === 'personal' }"
          @click="activeTab = 'personal'"
        >
          {{ $t('login.tab.personal') }}
        </div>
        <div
          class="tab-item"
          :class="{ active: activeTab === 'enterprise' }"
          @click="activeTab = 'enterprise'"
        >
          {{ $t('login.tab.enterprise') }}
        </div>
      </div>

      <!-- Personal Login Component -->
      <PersonalLogin
        v-if="activeTab === 'personal'"
        ref="personalLogin"
        :visible="visible && activeTab === 'personal'"
        @login-success="handleLoginSuccess"
        @privacy-click="handlePrivacyClick"
        @register-click="handleRegisterClick"
      />

      <!-- Enterprise Login Component -->
      <EnterpriseLogin
        v-if="activeTab === 'enterprise'"
        ref="enterpriseLogin"
        :visible="visible && activeTab === 'enterprise'"
        @login-success="handleLoginSuccess"
        @privacy-click="handlePrivacyClick"
        @register-click="handleRegisterClick"
      />
    </div>
  </el-dialog>
</template>

<script>
import PersonalLogin from './PersonalLogin.vue'
import EnterpriseLogin from './EnterpriseLogin.vue'

export default {
  name: 'LoginDialog',
  components: {
    PersonalLogin,
    EnterpriseLogin
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    dialogWidth: {
      type: String,
      default: '550px'
    }
  },
  data() {
    return {
      activeTab: 'personal', // 'personal' or 'enterprise'
      isMobile: false
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.checkMobile()
      } else {
        this.resetForm()
      }
    },
    activeTab() {
      // 切换tab时重置表单
      if (this.$refs.personalLogin) {
        this.$refs.personalLogin.resetForm()
      }
      if (this.$refs.enterpriseLogin) {
        this.$refs.enterpriseLogin.resetForm()
      }
    }
  },
  mounted() {
    this.checkMobile()
    window.addEventListener('resize', this.checkMobile)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkMobile)
  },
  methods: {
    checkMobile() {
      this.isMobile = window.innerWidth <= 768
    },
    handleClose() {
      this.$emit('update:visible', false)
      this.resetForm()
    },
    resetForm() {
      this.activeTab = 'personal'
    },
    /**
     * 处理登录成功
     */
    handleLoginSuccess() {
      this.handleClose()
      // 延迟1秒后触发页面刷新或更新
      setTimeout(() => {
        this.$emit('login-success')
        window.location.reload()
      }, 1000)
    },
    handlePrivacyClick() {
      const route = this.$router.resolve({ name: 'UserPrivacyPolicy' })
      window.open(route.href, '_blank', 'noopener,noreferrer')
    },
    handleRegisterClick() {
      const route = this.$router.resolve({ name: 'UserRegisterAgreement' })
      window.open(route.href, '_blank', 'noopener,noreferrer')
    }
  }
}
</script>

<style lang="scss" scoped>
.login-container {
  position: relative;
  padding: 18px 40px 40px;
  min-height: 500px;

  @media (max-width: 768px) {
    padding: 15px 20px 30px;
    min-height: auto;
  }
}

.login-title {
  text-align: left;
  margin-bottom: 30px;
  padding: 0 4px;

  @media (max-width: 768px) {
    margin-bottom: 20px;
  }

  .title-main {
    font-size: 18px;
    color: #333;
    margin-bottom: 8px;
    font-weight: 500;
    line-height: 1.5;

    @media (max-width: 768px) {
      font-size: 16px;
      margin-bottom: 4px;
    }
  }

  .title-sub {
    font-size: 23px;
    color: #333;
    font-weight: 600;
    line-height: 1.4;
    letter-spacing: 0.5px;

    @media (max-width: 768px) {
      font-size: 18px;
    }
  }
}

.login-tabs {
  display: flex;
  margin-bottom: 20px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #0d63f3;

  @media (max-width: 768px) {
    margin-bottom: 15px;
  }

  .tab-item {
    flex: 1;
    text-align: center;
    padding: 12px 0;
    font-size: 14px;
    color: #333;
    cursor: pointer;
    transition: all 0.3s;
    background-color: #fff;
    border-right: 1px solid #0d63f3;
    font-weight: 500;

    &:first-child {
      border-top-left-radius: 8px;
      border-bottom-left-radius: 8px;
    }

    &:last-child {
      border-right: none;
      border-top-right-radius: 8px;
      border-bottom-right-radius: 8px;
    }

    &:hover:not(.active) {
      background-color: #f5f7ff;
    }

    &.active {
      color: #fff;
      background-color: #0d63f3;
      font-weight: 400;
      border-right: 1px solid #0d63f3;
    }
  }
}
</style>

<style lang="scss">
.login-dialog {
  background: #fff !important;
  border-radius: 12px !important;
  
  .el-dialog__header {
    padding: 0;
    margin: 0;
    height: 0;

    .el-dialog__headerbtn {
      top: 15px;
      right: 15px;
      z-index: 100;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;

      .el-dialog__close {
        font-size: 22px;
        color: #909399;
        
        &:hover {
          color: #409eff;
        }
      }
    }
  }

  .el-dialog__body {
    padding: 0;
  }

  @media (max-width: 768px) {
    width: 95% !important;
    margin: 10vh auto !important;
    max-width: none !important;
    top: 0 !important;

    .el-dialog__header {
      .el-dialog__headerbtn {
        top: 10px;
        right: 10px;
      }
    }
  }
}
</style>
